import { LoadingStatus } from '@bcef/ia-shared';
import { createFeatureSelector, createSelector, MemoizedSelector } from '@ngrx/store';

import { SimulationCharacteristics } from '@features/loan/interfaces/simulation-characteristics.interface';
import { Loan, LoanCharacteristics, LoanFamily } from '@features/loan/models';
import {
  Project,
  PublishablesAreas,
} from '@features/loan/models/publishables-areas-response.model';
import * as fromFeature from '@features/loan/store/reducers';
import * as fromRequests from '@features/loan/store/reducers/loan.reducer';
import { InputForPpiRedirection } from '@features/loan/types/input-for-ppi-redirection.type';

const FIRST_NOT_ELIGIBLE_VALUE = '005';

const SECOND_NOT_ELIGIBLE_VALUE = '002';

export const selectAppLoanState = createFeatureSelector<fromFeature.PpiSimulatorState>(
  fromFeature.featureName,
);

export const selectCatalogsLoadingStatus = createSelector(
  selectAppLoanState,
  (state: fromRequests.PpiSimulatorState): LoadingStatus => state.catalogLoadingStatus,
);

export const selectCatalogsIsLoaded = createSelector(
  selectCatalogsLoadingStatus,
  (status: LoadingStatus): boolean => status.isLoaded,
);
export const selectLoanCreationLoadingStatus = createSelector(
  selectAppLoanState,
  (state: fromRequests.PpiSimulatorState): LoadingStatus => state.loanCreationLoadingStatus,
);

export const selectLoanCreationIsLoading = createSelector(
  selectLoanCreationLoadingStatus,
  (status: LoadingStatus): boolean => status.isLoading,
);

export const selectJointCaseLoanCreationLoadingStatus = createSelector(
  selectAppLoanState,
  (state: fromRequests.PpiSimulatorState): LoadingStatus =>
    state.jointCaseLoanCreationLoadingStatus,
);

export const selectJointCaseLoanCreationLoadingIsLoading = createSelector(
  selectJointCaseLoanCreationLoadingStatus,
  (status: LoadingStatus): boolean => status.isLoading,
);

export const selectConnectedSimulationLoadingStatus = createSelector(
  selectAppLoanState,
  (state: fromRequests.PpiSimulatorState): LoadingStatus => state.connectedSimulationLoadingStatus,
);

export const selectConnectedSimulationIsLoading = createSelector(
  selectConnectedSimulationLoadingStatus,
  (status: LoadingStatus): boolean => status.isLoading,
);

export const selectNotConnectedSimulationLoadingStatus = createSelector(
  selectAppLoanState,
  (state: fromRequests.PpiSimulatorState): LoadingStatus =>
    state.notConnectedSimulationLoadingStatus,
);

export const selectNotConnectedSimulationIsLoading = createSelector(
  selectNotConnectedSimulationLoadingStatus,
  (status: LoadingStatus): boolean => status.isLoading,
);

export const selectListDuration = createSelector(
  selectAppLoanState,
  (state: fromRequests.PpiSimulatorState): number[] | undefined => state.catalog.durations,
);

export const selectLoanFamilies = createSelector(
  selectAppLoanState,
  (state: fromRequests.PpiSimulatorState): LoanFamily[] | undefined => state.catalog.families,
);

export const selectSimulationCharacteristics = createSelector(
  selectAppLoanState,
  (state: fromRequests.PpiSimulatorState): SimulationCharacteristics =>
    state.simulationCharacteristics,
);

export const selectLoans = createSelector(
  selectLoanFamilies,
  (families: LoanFamily[] | undefined): Loan[] => {
    if (!families) {
      return [];
    }
    return families.flatMap((element) => element.loans);
  },
);

export const selectLoanCharacteristics = (
  index: number,
): MemoizedSelector<object, Loan, (s1: Loan[]) => Loan> =>
  createSelector(selectLoans, (loans: Loan[]): Loan => loans[index]);

export const selectLoanListDuration = (
  index: number,
): MemoizedSelector<
  object,
  number[] | undefined,
  (s1: Loan, s2: number[] | undefined) => number[] | undefined
> =>
  createSelector(
    selectLoanCharacteristics(index),
    selectListDuration,
    (loans: Loan, durationList: number[] | undefined): number[] | undefined =>
      durationList?.filter((element) => element <= loans.maxDuration),
  );

export const selectIsConnected = createSelector(
  selectAppLoanState,
  (state: fromRequests.PpiSimulatorState): boolean | undefined => state.isConnected,
);

export const selectProjects = createSelector(
  selectAppLoanState,
  (state: fromRequests.PpiSimulatorState): LoanCharacteristics => state.creationResponse,
);

export const selectProjectType = createSelector(
  selectAppLoanState,
  selectLoans,
  (state: fromRequests.PpiSimulatorState, loans: Loan[]): string | undefined => {
    const result = loans.find((loan) => loan.id === state.selectedProjectType);
    return result?.label;
  },
);

export const selectCalculationByMonthlyPayment = createSelector(
  selectAppLoanState,
  (state: fromRequests.PpiSimulatorState): boolean | undefined => state.calculationByMonthlyPayment,
);

export const selectSimulationIsEligible = createSelector(
  selectAppLoanState,
  selectIsConnected,
  (state: fromRequests.PpiSimulatorState, isConnected: boolean | undefined): boolean =>
    (isConnected && state.simulationCharacteristics.isLoanEligible !== FIRST_NOT_ELIGIBLE_VALUE) ||
    (!isConnected &&
      state.simulationCharacteristics.isLoanEligible !== FIRST_NOT_ELIGIBLE_VALUE &&
      state.simulationCharacteristics.isLoanEligible !== SECOND_NOT_ELIGIBLE_VALUE),
);

export const selectHasPromotionalOffer = createSelector(
  selectAppLoanState,
  (state: fromRequests.PpiSimulatorState): boolean =>
    state.simulationCharacteristics.isPromotionalOffer,
);

export const selectHasEmployeeOffer = createSelector(
  selectAppLoanState,
  (state: fromRequests.PpiSimulatorState): boolean =>
    !!state.simulationCharacteristics.isEmployeeOffer,
);

export const selectConnectedBorrowerIndicators = createSelector(
  selectProjects,
  selectSimulationCharacteristics,
  (loan: LoanCharacteristics, simulation: SimulationCharacteristics): InputForPpiRedirection => ({
    id: loan.id,
    type: loan.projectTypeId,
    eligibility: simulation.isLoanEligible,
    clientType: loan.customerType,
    simulationRef: simulation.simulationReference,
  }),
);

export const selectPublishablesAreas = createSelector(
  selectAppLoanState,
  (state: fromRequests.PpiSimulatorState): PublishablesAreas => state.areas,
);

export const selectProjectsPublishablesAreas = createSelector(
  selectPublishablesAreas,
  (state: PublishablesAreas) =>
    state.projects.reduce(
      (accumlator, project) => {
        accumlator[project.code] = project;
        return accumlator;
      },
      {} as Record<string, Project>,
    ),
);

export const selectJointCaseProject = createSelector(
  selectAppLoanState,
  (state: fromRequests.PpiSimulatorState): LoanCharacteristics => state.jointCaseCreationResponse,
);

export const selectJointCaseSimulationCharacteristics = createSelector(
  selectAppLoanState,
  (state: fromRequests.PpiSimulatorState): SimulationCharacteristics =>
    state.jointCaseSimulationCharacteristics,
);

export const selectSaveSimulationLoadingStatus = createSelector(
  selectAppLoanState,
  (state: fromRequests.PpiSimulatorState): LoadingStatus => state.saveSimulationLoadingStatus,
);

export const selectSaveSimulationLoadingIsLoading = createSelector(
  selectSaveSimulationLoadingStatus,
  (status: LoadingStatus): boolean => status.isLoading,
);

export const selectCoBorrowerIndicators = createSelector(
  selectJointCaseProject,
  selectJointCaseSimulationCharacteristics,
  (loan: LoanCharacteristics, simulation: SimulationCharacteristics): InputForPpiRedirection => ({
    id: loan.id,
    type: loan.projectTypeId,
    eligibility: simulation?.isLoanEligible,
    simulationRef: simulation?.simulationReference,
  }),
);

export const selectIsSingleBorrower = createSelector(
  selectAppLoanState,
  (state: fromRequests.PpiSimulatorState): boolean => state.isSingleBorrower,
);
